import { AsYouType, getCountries, getCountryCallingCode, parsePhoneNumberFromString, type CountryCode } from "libphonenumber-js";

export const enquiryInterests = [
  { value: "mission-planning", label: "Mission planning & readiness" },
  { value: "team-and-fleet", label: "Team, scheduling & fleet management" },
  { value: "delivery-and-reporting", label: "Delivery, review & reporting" },
  { value: "ai-operations", label: "AI agents & operational automation" },
  { value: "enterprise-program", label: "Enterprise drone programs" },
  { value: "integrations", label: "Systems & integrations" },
  { value: "security-and-governance", label: "Security, compliance & governance" },
  { value: "other", label: "Other — tell us below" },
] as const;
export const enquiryPhoneCountries = getCountries();
export type EnquiryPhoneCountryOption = Readonly<{ country: CountryCode; label: string }>;
export const defaultEnquiryPhoneCountryOptions: readonly EnquiryPhoneCountryOption[] = enquiryPhoneCountries.map(country => ({ country, label: `${country} (+${getCountryCallingCode(country)})` }));
/** Run on the server and serialize the labels so browser ICU versions cannot change hydration text. */
export function buildEnquiryPhoneCountryOptions(locale: string): EnquiryPhoneCountryOption[] {
  const names = new Intl.DisplayNames([locale], { type: "region" });
  return enquiryPhoneCountries.map(country => ({ country, label: `${names.of(country) ?? country} (+${getCountryCallingCode(country)})` })).sort((a,b) => a.label.localeCompare(b.label, locale));
}
export function isEnquiryPhoneCountry(value: string): value is CountryCode {
  return enquiryPhoneCountries.includes(value as CountryCode);
}
export { getCountryCallingCode };
export function formatEnquiryPhone(value: string, country: string): string {
  if (!isEnquiryPhoneCountry(country)) return value;
  if (value.trim().startsWith("+")) {
    const parsed = parsePhoneNumberFromString(value, { extract: false });
    return parsed?.country === country ? parsed.formatNational() : value;
  }
  return new AsYouType(country).input(value);
}
export function normalizeEnquiryPhone(value: string, country: string): string | null {
  if (!isEnquiryPhoneCountry(country) || value.length > 50 || !/^[+\d\s().-]+$/.test(value)) return null;
  const parsed = parsePhoneNumberFromString(value, { defaultCountry: country, extract: false });
  return parsed?.country === country && parsed.isValid() ? parsed.number : null;
}
export function normalizeCompanyWebsite(value: string): string | null {
  const input = value.trim();
  if (!input || input.length > 254 || /[\s\u0000-\u001f\u007f]/.test(input)) return null;
  try {
    const url = new URL(/^[a-z][a-z\d+.-]*:/i.test(input) ? input : `https://${input}`);
    return ["https:", "http:"].includes(url.protocol) && !url.username && !url.password && url.hostname.includes(".") && url.hostname.split(".").every(label => /^[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?$/i.test(label)) && url.href.length <= 254 ? url.href : null;
  } catch { return null; }
}
